import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Waitlist modal for City Rides launch interest.
 * Captures name + phone, posts to /api/waitlist (rate-limited 10/hour/IP).
 * On success, shows a confirmation card (not just a toast) so the user
 * is confident their action worked.
 */
const WaitlistModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);

  // Reset state when reopened
  useEffect(() => {
    if (isOpen) {
      setName("");
      setPhone("");
      setSubmitting(false);
      setSubmitted(false);
      setAlreadyJoined(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    const phoneClean = (() => {
      let p = phone.replace(/[\s\-()]/g, "");
      if (p.startsWith("+91")) p = p.slice(3);
      else if (p.startsWith("91") && p.length === 12) p = p.slice(2);
      return p;
    })();
    if (!/^[6-9]\d{9}$/.test(phoneClean)) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/waitlist`, { name: name.trim(), phone: phoneClean, service: "city_rides" });
      setAlreadyJoined(!!resp.data?.alreadyJoined);
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't add you to the waitlist. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't add you to the waitlist. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={onClose}
          data-testid="waitlist-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-sarathi-surface w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-white/10 p-6 sm:p-7 relative max-h-[90vh] overflow-y-auto"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="waitlist-close"
            >
              <X className="w-4 h-4" />
            </button>

            {!submitted ? (
              <>
                <div className="mb-5">
                  <span className="inline-block px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-400 text-[10px] font-bold tracking-wider border border-amber-500/30 uppercase mb-3">
                    Coming Soon
                  </span>
                  <h2 className="font-outfit text-2xl font-bold text-white tracking-tight mb-1.5">
                    Join the City Rides waitlist
                  </h2>
                  <p className="text-sarathi-text-secondary text-sm leading-relaxed">
                    We'll text you the moment city rides go live in Bengaluru.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="waitlist-name" className="block text-xs font-semibold text-sarathi-text-secondary uppercase tracking-wider mb-1.5">
                      Name
                    </label>
                    <input
                      id="waitlist-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      maxLength={100}
                      required
                      autoFocus
                      className="w-full bg-sarathi-background border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-sarathi-primary/50 transition-colors"
                      data-testid="waitlist-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="waitlist-phone" className="block text-xs font-semibold text-sarathi-text-secondary uppercase tracking-wider mb-1.5">
                      Phone Number
                    </label>
                    <input
                      id="waitlist-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="10-digit mobile (e.g. 98765 43210)"
                      maxLength={20}
                      required
                      inputMode="tel"
                      className="w-full bg-sarathi-background border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-sarathi-primary/50 transition-colors"
                      data-testid="waitlist-phone-input"
                    />
                    <p className="text-[11px] text-sarathi-text-muted mt-1.5">
                      We'll only use this to notify you when city rides launch.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 rounded-xl bg-sarathi-primary hover:bg-orange-600 text-white font-bold text-[15px] tracking-tight disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-[0_4px_24px_rgba(249,115,22,0.25)]"
                    data-testid="waitlist-submit"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Adding you to the list…
                      </span>
                    ) : (
                      "Notify me at launch"
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-3" data-testid="waitlist-success">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" strokeWidth={2.4} />
                </div>
                <h2 className="font-outfit text-xl font-bold text-white tracking-tight mb-2">
                  {alreadyJoined ? "You're already on the list" : "You're on the list!"}
                </h2>
                <p className="text-sarathi-text-secondary text-sm leading-relaxed mb-1">
                  {alreadyJoined
                    ? "We've updated your details. We'll text you when city rides launch."
                    : "We'll text you when city rides launch."}
                </p>
                <p className="text-sarathi-text-muted text-xs mb-6">
                  In the meantime, our airport transfers are live and available 24×7.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-3.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm transition-colors"
                  data-testid="waitlist-success-close"
                >
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WaitlistModal;
